import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  ArrowDownCircle,
  ArrowUpCircle,
  Wrench,
  Calendar,
  Filter,
  Search,
  Plus,
  Minus,
  ShieldCheck,
} from "lucide-react";
import UnitScopeTabs from "../../components/UnitScopeTabs";

const BRT_TZ = "America/Sao_Paulo";
const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      timeZone: BRT_TZ,
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};
const money = (v) => `R$ ${Number(v || 0).toFixed(2).replace(".", ",")}`;

const MOTIVOS_SAIDA = [
  "Perda",
  "Quebra / Avaria",
  "Vencimento",
  "Uso interno",
  "Ajuste de inventário",
  "Devolução ao fornecedor",
  "Outro",
];

export default function MovimentacoesEstoque() {
  const [unidades, setUnidades] = useState([]);
  const [aba, setAba] = useState("entrada"); // entrada | saida | historico

  useEffect(() => {
    api.get("/unidades").then((r) => setUnidades(r.data));
  }, []);

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-neutral-500">
            Auditável · Custo médio ponderado
          </p>
          <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">
            Movimentações de Estoque
          </h2>
        </div>
      </div>

      {/* Tabs internas */}
      <div className="flex border-b-2 border-neutral-900 bg-white">
        <TabBtn
          id="entrada"
          active={aba === "entrada"}
          onClick={() => setAba("entrada")}
          icon={<ArrowDownCircle className="w-4 h-4" />}
          label="Entrada"
          accent="bg-green-50"
        />
        <TabBtn
          id="saida"
          active={aba === "saida"}
          onClick={() => setAba("saida")}
          icon={<ArrowUpCircle className="w-4 h-4" />}
          label="Saída / Correção"
          accent="bg-red-50"
        />
        <TabBtn
          id="historico"
          active={aba === "historico"}
          onClick={() => setAba("historico")}
          icon={<Wrench className="w-4 h-4" />}
          label="Histórico"
          accent="bg-yellow-50"
        />
      </div>

      {aba === "entrada" && <EntradaForm unidades={unidades} />}
      {aba === "saida" && <SaidaForm unidades={unidades} />}
      {aba === "historico" && <Historico unidades={unidades} />}
    </div>
  );
}

const TabBtn = ({ id, active, onClick, icon, label, accent }) => (
  <button
    data-testid={`mov-tab-${id}`}
    onClick={onClick}
    className={`flex-1 sm:flex-none px-4 h-11 flex items-center justify-center gap-2 font-display font-bold uppercase text-xs tracking-widest border-r-2 border-neutral-900 last:border-r-0 ${
      active
        ? `${accent} text-neutral-950`
        : "bg-white text-neutral-500 hover:bg-neutral-100"
    }`}
  >
    {icon} {label}
  </button>
);

/* ---------------------------------------------------------------- ENTRADA */
function EntradaForm({ unidades }) {
  const [unidadeId, setUnidadeId] = useState("");
  const [estoque, setEstoque] = useState([]);
  const [busca, setBusca] = useState("");
  const [selecionado, setSelecionado] = useState(null);
  const [qtd, setQtd] = useState(1);
  const [precoCompra, setPrecoCompra] = useState("");
  const [fornecedor, setFornecedor] = useState("");
  const [notaFiscal, setNotaFiscal] = useState("");
  const [observacao, setObservacao] = useState("");
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!unidadeId) {
      setEstoque([]);
      return;
    }
    api.get("/estoque", { params: { unidade_id: unidadeId } }).then((r) => setEstoque(r.data));
  }, [unidadeId]);

  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return estoque.slice(0, 12);
    return estoque
      .filter((e) =>
        `${e.produto_nome} ${e.categoria}`.toLowerCase().includes(q),
      )
      .slice(0, 30);
  }, [estoque, busca]);

  const previewNovoCusto = useMemo(() => {
    if (!selecionado || !qtd || precoCompra === "") return null;
    const qAtual = Number(selecionado.quantidade_atual || 0);
    const cAtual = Number(selecionado.preco_custo || 0);
    const qEnt = Number(qtd);
    const pC = Number(precoCompra);
    const base = Math.max(qAtual, 0);
    const denom = base + qEnt;
    if (denom <= 0) return pC;
    return (base * cAtual + qEnt * pC) / denom;
  }, [selecionado, qtd, precoCompra]);

  const submit = async () => {
    if (!unidadeId) return toast.error("Selecione a unidade");
    if (!selecionado) return toast.error("Selecione um produto");
    if (!qtd || Number(qtd) <= 0) return toast.error("Quantidade inválida");
    if (precoCompra === "" || Number(precoCompra) < 0)
      return toast.error("Preço de compra inválido");
    try {
      setSalvando(true);
      const r = await api.post("/estoque/movimentacoes/entrada", {
        unidade_id: unidadeId,
        produto_id: selecionado.produto_id,
        quantidade: Number(qtd),
        preco_compra_unitario: Number(precoCompra),
        fornecedor,
        nota_fiscal: notaFiscal,
        observacao,
      });
      toast.success(
        `Entrada registrada. Novo custo médio: ${money(r.data.custo_medio_atual)}`,
      );
      // reload and reset
      const est = await api.get("/estoque", { params: { unidade_id: unidadeId } });
      setEstoque(est.data);
      const atualizado = est.data.find((e) => e.produto_id === selecionado.produto_id);
      setSelecionado(atualizado || null);
      setQtd(1);
      setPrecoCompra("");
      setFornecedor("");
      setNotaFiscal("");
      setObservacao("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao registrar entrada");
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="bg-white border-2 border-t-0 border-neutral-900 p-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div>
        <L label="Unidade">
          <select
            data-testid="entrada-unidade"
            value={unidadeId}
            onChange={(e) => {
              setUnidadeId(e.target.value);
              setSelecionado(null);
            }}
            className="mt-1 w-full h-11 px-3 border-2 border-neutral-900 font-mono bg-white"
          >
            <option value="">— Selecione —</option>
            {unidades.map((u) => (
              <option key={u.id} value={u.id}>
                {u.nome}
              </option>
            ))}
          </select>
        </L>

        <div className="mt-3">
          <L label="Buscar produto">
            <div className="relative mt-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
              <input
                data-testid="entrada-busca-produto"
                disabled={!unidadeId}
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder={
                  unidadeId ? "Digite o nome do produto..." : "Selecione a unidade primeiro"
                }
                className="w-full h-11 pl-9 pr-3 border-2 border-neutral-900 font-mono disabled:bg-neutral-100"
              />
            </div>
          </L>
          {unidadeId && (
            <div className="mt-2 border-2 border-neutral-900 max-h-56 overflow-y-auto">
              {filtrados.length === 0 && (
                <div className="p-3 text-xs text-neutral-500">Nenhum produto.</div>
              )}
              {filtrados.map((e) => (
                <button
                  key={e.id}
                  data-testid={`entrada-item-${e.produto_id}`}
                  onClick={() => setSelecionado(e)}
                  className={`w-full text-left px-3 py-2 border-b border-neutral-200 hover:bg-neutral-50 flex items-center justify-between ${
                    selecionado?.produto_id === e.produto_id ? "bg-green-50" : ""
                  }`}
                >
                  <div>
                    <div className="font-display font-bold text-sm">{e.produto_nome}</div>
                    <div className="font-mono text-[10px] text-neutral-500 uppercase">
                      {e.categoria} · Estoque {e.quantidade_atual} · Custo médio{" "}
                      {money(e.preco_custo)}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div>
        {selecionado ? (
          <div className="border-2 border-neutral-900 p-3 bg-neutral-50">
            <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
              Produto selecionado
            </div>
            <div className="font-display font-black text-lg">{selecionado.produto_nome}</div>
            <div className="grid grid-cols-3 gap-2 mt-2 text-center">
              <Mini label="Estoque" value={selecionado.quantidade_atual} />
              <Mini label="Custo médio atual" value={money(selecionado.preco_custo)} />
              <Mini label="Preço venda" value={money(selecionado.preco_venda)} />
            </div>
          </div>
        ) : (
          <div className="border-2 border-dashed border-neutral-400 p-6 text-center text-sm text-neutral-500">
            Selecione um produto para lançar a entrada.
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 mt-3">
          <L label="Quantidade">
            <input
              data-testid="entrada-qtd"
              type="number"
              min="1"
              value={qtd}
              onChange={(e) => setQtd(e.target.value)}
              className="mt-1 w-full h-11 px-3 border-2 border-neutral-900 font-mono"
            />
          </L>
          <L label="Preço de compra unitário (R$)">
            <input
              data-testid="entrada-preco"
              type="number"
              step="0.01"
              value={precoCompra}
              onChange={(e) => setPrecoCompra(e.target.value)}
              className="mt-1 w-full h-11 px-3 border-2 border-neutral-900 font-mono"
            />
          </L>
          <L label="Fornecedor (opcional)">
            <input
              data-testid="entrada-fornecedor"
              value={fornecedor}
              onChange={(e) => setFornecedor(e.target.value)}
              className="mt-1 w-full h-11 px-3 border-2 border-neutral-900 font-mono"
            />
          </L>
          <L label="Nota fiscal (opcional)">
            <input
              data-testid="entrada-nf"
              value={notaFiscal}
              onChange={(e) => setNotaFiscal(e.target.value)}
              className="mt-1 w-full h-11 px-3 border-2 border-neutral-900 font-mono"
            />
          </L>
        </div>
        <L label="Observação (opcional)">
          <input
            data-testid="entrada-obs"
            value={observacao}
            onChange={(e) => setObservacao(e.target.value)}
            className="mt-1 w-full h-11 px-3 border-2 border-neutral-900 font-mono"
          />
        </L>

        {previewNovoCusto !== null && Number.isFinite(previewNovoCusto) && (
          <div className="mt-3 border-l-4 border-green-600 bg-green-50 p-3 font-mono text-sm">
            <div className="text-[10px] uppercase tracking-widest text-neutral-600">
              Preview
            </div>
            <div>
              Novo custo médio ponderado:{" "}
              <span className="font-black">{money(previewNovoCusto)}</span>
            </div>
            <div className="text-xs text-neutral-600 mt-0.5">
              (
              {Math.max(Number(selecionado?.quantidade_atual || 0), 0)} ×{" "}
              {money(selecionado?.preco_custo)} + {Number(qtd)} × {money(precoCompra)}) /{" "}
              {Math.max(Number(selecionado?.quantidade_atual || 0), 0) + Number(qtd)}
            </div>
          </div>
        )}

        <button
          data-testid="entrada-submit"
          disabled={salvando || !selecionado || !unidadeId}
          onClick={submit}
          className="mt-4 w-full bg-green-600 hover:bg-green-700 disabled:bg-neutral-300 text-white h-12 font-display font-bold uppercase text-xs tracking-widest flex items-center justify-center gap-2 cta-brutal"
        >
          <Plus className="w-4 h-4" />
          {salvando ? "Registrando..." : "Confirmar entrada"}
        </button>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- SAÍDA */
function SaidaForm({ unidades }) {
  const [unidadeId, setUnidadeId] = useState("");
  const [estoque, setEstoque] = useState([]);
  const [busca, setBusca] = useState("");
  const [selecionado, setSelecionado] = useState(null);
  const [qtd, setQtd] = useState(1);
  const [motivo, setMotivo] = useState(MOTIVOS_SAIDA[0]);
  const [observacao, setObservacao] = useState("");
  const [senha, setSenha] = useState("");
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!unidadeId) {
      setEstoque([]);
      return;
    }
    api.get("/estoque", { params: { unidade_id: unidadeId } }).then((r) => setEstoque(r.data));
  }, [unidadeId]);

  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return estoque.slice(0, 12);
    return estoque
      .filter((e) =>
        `${e.produto_nome} ${e.categoria}`.toLowerCase().includes(q),
      )
      .slice(0, 30);
  }, [estoque, busca]);

  const submit = async () => {
    if (!unidadeId) return toast.error("Selecione a unidade");
    if (!selecionado) return toast.error("Selecione um produto");
    if (!qtd || Number(qtd) <= 0) return toast.error("Quantidade inválida");
    if (!motivo) return toast.error("Informe o motivo");
    if (!senha) return toast.error("PIN Master obrigatório");
    try {
      setSalvando(true);
      await api.post("/estoque/movimentacoes/saida", {
        unidade_id: unidadeId,
        produto_id: selecionado.produto_id,
        quantidade: Number(qtd),
        motivo,
        observacao,
        senha_master: senha,
      });
      toast.success("Saída registrada com sucesso");
      const est = await api.get("/estoque", { params: { unidade_id: unidadeId } });
      setEstoque(est.data);
      const atualizado = est.data.find((e) => e.produto_id === selecionado.produto_id);
      setSelecionado(atualizado || null);
      setQtd(1);
      setObservacao("");
      setSenha("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao registrar saída");
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="bg-white border-2 border-t-0 border-neutral-900 p-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div>
        <L label="Unidade">
          <select
            data-testid="saida-unidade"
            value={unidadeId}
            onChange={(e) => {
              setUnidadeId(e.target.value);
              setSelecionado(null);
            }}
            className="mt-1 w-full h-11 px-3 border-2 border-neutral-900 font-mono bg-white"
          >
            <option value="">— Selecione —</option>
            {unidades.map((u) => (
              <option key={u.id} value={u.id}>
                {u.nome}
              </option>
            ))}
          </select>
        </L>

        <div className="mt-3">
          <L label="Buscar produto">
            <div className="relative mt-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
              <input
                data-testid="saida-busca-produto"
                disabled={!unidadeId}
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder={
                  unidadeId ? "Digite o nome do produto..." : "Selecione a unidade primeiro"
                }
                className="w-full h-11 pl-9 pr-3 border-2 border-neutral-900 font-mono disabled:bg-neutral-100"
              />
            </div>
          </L>
          {unidadeId && (
            <div className="mt-2 border-2 border-neutral-900 max-h-56 overflow-y-auto">
              {filtrados.length === 0 && (
                <div className="p-3 text-xs text-neutral-500">Nenhum produto.</div>
              )}
              {filtrados.map((e) => (
                <button
                  key={e.id}
                  data-testid={`saida-item-${e.produto_id}`}
                  onClick={() => setSelecionado(e)}
                  className={`w-full text-left px-3 py-2 border-b border-neutral-200 hover:bg-neutral-50 flex items-center justify-between ${
                    selecionado?.produto_id === e.produto_id ? "bg-red-50" : ""
                  }`}
                >
                  <div>
                    <div className="font-display font-bold text-sm">{e.produto_nome}</div>
                    <div className="font-mono text-[10px] text-neutral-500 uppercase">
                      {e.categoria} · Estoque {e.quantidade_atual} · Custo médio{" "}
                      {money(e.preco_custo)}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div>
        {selecionado ? (
          <div className="border-2 border-neutral-900 p-3 bg-neutral-50">
            <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
              Produto selecionado
            </div>
            <div className="font-display font-black text-lg">{selecionado.produto_nome}</div>
            <div className="grid grid-cols-2 gap-2 mt-2 text-center">
              <Mini label="Estoque atual" value={selecionado.quantidade_atual} />
              <Mini label="Custo médio" value={money(selecionado.preco_custo)} />
            </div>
          </div>
        ) : (
          <div className="border-2 border-dashed border-neutral-400 p-6 text-center text-sm text-neutral-500">
            Selecione um produto para lançar a saída/correção.
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 mt-3">
          <L label="Quantidade">
            <input
              data-testid="saida-qtd"
              type="number"
              min="1"
              value={qtd}
              onChange={(e) => setQtd(e.target.value)}
              className="mt-1 w-full h-11 px-3 border-2 border-neutral-900 font-mono"
            />
          </L>
          <L label="Motivo">
            <select
              data-testid="saida-motivo"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              className="mt-1 w-full h-11 px-3 border-2 border-neutral-900 font-mono bg-white"
            >
              {MOTIVOS_SAIDA.map((m) => (
                <option key={m}>{m}</option>
              ))}
            </select>
          </L>
        </div>
        <L label="Observação (opcional)">
          <input
            data-testid="saida-obs"
            value={observacao}
            onChange={(e) => setObservacao(e.target.value)}
            className="mt-1 w-full h-11 px-3 border-2 border-neutral-900 font-mono"
          />
        </L>

        <div className="mt-3 border-2 border-neutral-900 p-3 bg-yellow-50">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-neutral-700">
            <ShieldCheck className="w-4 h-4" /> Requer PIN Master
          </div>
          <input
            data-testid="saida-senha-master"
            type="password"
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            placeholder="•••••"
            className="mt-2 w-full h-11 px-3 border-2 border-neutral-900 font-mono tracking-widest text-center"
          />
        </div>

        <button
          data-testid="saida-submit"
          disabled={salvando || !selecionado || !unidadeId}
          onClick={submit}
          className="mt-4 w-full bg-red-600 hover:bg-red-700 disabled:bg-neutral-300 text-white h-12 font-display font-bold uppercase text-xs tracking-widest flex items-center justify-center gap-2 cta-brutal"
        >
          <Minus className="w-4 h-4" />
          {salvando ? "Registrando..." : "Confirmar saída"}
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- HISTÓRICO */
function Historico({ unidades }) {
  const [scope, setScope] = useState("global");
  const [tipo, setTipo] = useState("");
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [mov, setMov] = useState([]);

  const load = () => {
    const params = {};
    if (scope !== "global") params.unidade_id = scope;
    if (tipo) params.tipo = tipo;
    if (dataInicio) params.data_inicio = dataInicio;
    if (dataFim) params.data_fim = dataFim;
    api.get("/estoque/movimentacoes", { params }).then((r) => setMov(r.data));
  };

  useEffect(() => {
    load();
  }, [scope]); // eslint-disable-line

  const totalEntradas = mov
    .filter((m) => m.tipo === "entrada")
    .reduce((s, m) => s + (m.valor_total || 0), 0);
  const totalSaidas = mov
    .filter((m) => m.tipo === "saida")
    .reduce((s, m) => s + (m.valor_total || 0), 0);
  const totalAjustes = mov.filter((m) => m.tipo === "ajuste").length;

  const iconOf = (t) => {
    if (t === "entrada")
      return <ArrowDownCircle className="w-4 h-4 text-green-600" strokeWidth={2.5} />;
    if (t === "saida")
      return <ArrowUpCircle className="w-4 h-4 text-red-600" strokeWidth={2.5} />;
    return <Wrench className="w-4 h-4 text-yellow-600" strokeWidth={2.5} />;
  };

  return (
    <div className="bg-white border-2 border-t-0 border-neutral-900">
      <div className="p-3 border-b-2 border-neutral-900">
        <UnitScopeTabs unidades={unidades} value={scope} onChange={setScope} />
      </div>
      <div className="p-3 border-b-2 border-neutral-900 grid grid-cols-1 sm:grid-cols-5 gap-2">
        <select
          data-testid="hist-tipo"
          value={tipo}
          onChange={(e) => setTipo(e.target.value)}
          className="h-10 px-2 border-2 border-neutral-900 font-mono text-sm bg-white"
        >
          <option value="">Todos tipos</option>
          <option value="entrada">Entradas</option>
          <option value="saida">Saídas</option>
          <option value="ajuste">Ajustes</option>
        </select>
        <input
          data-testid="hist-inicio"
          type="date"
          value={dataInicio}
          onChange={(e) => setDataInicio(e.target.value)}
          className="h-10 px-2 border-2 border-neutral-900 font-mono text-sm"
        />
        <input
          data-testid="hist-fim"
          type="date"
          value={dataFim}
          onChange={(e) => setDataFim(e.target.value)}
          className="h-10 px-2 border-2 border-neutral-900 font-mono text-sm"
        />
        <button
          data-testid="hist-aplicar"
          onClick={load}
          className="bg-neutral-950 text-white h-10 px-4 font-display font-bold uppercase text-xs tracking-widest flex items-center justify-center gap-2"
        >
          <Filter className="w-4 h-4" /> Aplicar
        </button>
        <div className="h-10 flex items-center justify-end text-right font-mono text-xs text-neutral-600">
          <span className="font-bold">{mov.length}</span>&nbsp;movimentações
        </div>
      </div>

      <div className="grid grid-cols-3 gap-0 border-b-2 border-neutral-900">
        <Kpi
          label="Entradas (valor)"
          value={money(totalEntradas)}
          icon={<ArrowDownCircle className="w-4 h-4" />}
          accent="bg-green-50"
        />
        <Kpi
          label="Saídas (valor)"
          value={money(totalSaidas)}
          icon={<ArrowUpCircle className="w-4 h-4" />}
          accent="bg-red-50"
        />
        <Kpi
          label="Ajustes (qtd)"
          value={totalAjustes}
          icon={<Wrench className="w-4 h-4" />}
          accent="bg-yellow-50"
        />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-neutral-300 bg-neutral-50">
              <th className="text-left p-3 font-mono text-[10px] uppercase tracking-widest">
                Data
              </th>
              <th className="text-left p-3 font-mono text-[10px] uppercase tracking-widest">
                Tipo
              </th>
              <th className="text-left p-3 font-mono text-[10px] uppercase tracking-widest">
                Produto
              </th>
              {scope === "global" && (
                <th className="text-left p-3 font-mono text-[10px] uppercase tracking-widest">
                  Unidade
                </th>
              )}
              <th className="text-right p-3 font-mono text-[10px] uppercase tracking-widest">
                Qtd
              </th>
              <th className="text-right p-3 font-mono text-[10px] uppercase tracking-widest">
                Val. Unit.
              </th>
              <th className="text-right p-3 font-mono text-[10px] uppercase tracking-widest">
                Val. Total
              </th>
              <th className="text-left p-3 font-mono text-[10px] uppercase tracking-widest">
                Motivo / Ref.
              </th>
            </tr>
          </thead>
          <tbody>
            {mov.map((m) => (
              <tr key={m.id} className="border-b border-neutral-100 align-top">
                <td className="p-3 font-mono text-xs whitespace-nowrap">
                  <Calendar className="w-3 h-3 inline mr-1 text-neutral-400" />
                  {fmtDate(m.created_at)}
                </td>
                <td className="p-3">
                  <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest">
                    {iconOf(m.tipo)} {m.tipo}
                  </span>
                </td>
                <td className="p-3">
                  <div className="font-display font-bold">{m.produto_nome}</div>
                  {(m.custo_medio_anterior !== null && m.custo_medio_posterior !== null && m.tipo === "entrada") && (
                    <div className="font-mono text-[10px] text-neutral-500">
                      Custo médio: {money(m.custo_medio_anterior)} →{" "}
                      <span className="font-bold text-neutral-900">
                        {money(m.custo_medio_posterior)}
                      </span>
                    </div>
                  )}
                </td>
                {scope === "global" && (
                  <td className="p-3 font-mono text-xs">{m.unidade_nome}</td>
                )}
                <td
                  className={`p-3 text-right font-mono font-bold ${
                    m.tipo === "saida" ? "text-red-600" : "text-green-700"
                  }`}
                >
                  {m.tipo === "saida" ? "−" : "+"}
                  {m.quantidade}
                </td>
                <td className="p-3 text-right font-mono text-xs">
                  {money(m.valor_unitario_efetivo)}
                </td>
                <td className="p-3 text-right font-mono font-bold">
                  {money(m.valor_total)}
                </td>
                <td className="p-3 font-mono text-xs text-neutral-600">
                  {m.motivo || m.origem}
                </td>
              </tr>
            ))}
            {mov.length === 0 && (
              <tr>
                <td
                  colSpan={scope === "global" ? 8 : 7}
                  className="p-8 text-center text-sm text-neutral-500"
                >
                  Nenhuma movimentação no período/filtro.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Kpi = ({ label, value, icon, accent }) => (
  <div className={`p-3 ${accent} border-r-2 border-neutral-900 last:border-r-0`}>
    <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-600 flex items-center gap-1">
      {icon} {label}
    </div>
    <div className="font-display font-black text-xl mt-1">{value}</div>
  </div>
);

const Mini = ({ label, value }) => (
  <div className="border border-neutral-300 bg-white p-2">
    <div className="font-mono text-[9px] uppercase tracking-widest text-neutral-500">
      {label}
    </div>
    <div className="font-display font-black text-sm">{value}</div>
  </div>
);

const L = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
      {label}
    </span>
    {children}
  </label>
);
